import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  BookOpenIcon,
  MagnifyingGlassIcon,
  UserIcon,
  XMarkIcon,
  LockClosedIcon,
  LockOpenIcon,
  CreditCardIcon,
  PhoneIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartOutline } from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolid } from "@heroicons/react/24/solid";
import apiClient from "../../api/client";
import toast from "react-hot-toast";

// PDF Viewer
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const API_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1"
).replace(/\/+$/, "");

const API_BASE_URL = API_URL.replace(/\/api\/v1$/, "");

const SCIENCE_SECTORS = ["Science", "Chemistry"];
const BOOKS_PER_PAGE = 20;
const INITIAL_SKELETON_COUNT = 8;
const EAGER_COVER_COUNT = 6;
const HIGH_PRIORITY_COVER_COUNT = 4;
const ACTIVE_BOOK_PAYMENT_KEY = "activeBookPayment";
const DISMISSED_BOOK_PAYMENT_KEY = "dismissedBookPayment";

// Self-contained helpers for the PawaSchool frontend.
const BOOK_CACHE_KEY = "pawaschool:library:books";
const DOWNLOADED_IDS_KEY = "pawaschool:library:downloaded-ids";
const PDF_CACHE_NAME = "pawaschool-library-pdfs-v1";

const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return isOnline;
};

const cacheBooks = async (books) => {
  localStorage.setItem(BOOK_CACHE_KEY, JSON.stringify(books || []));
};

const getCachedBooks = async () => {
  try {
    return JSON.parse(localStorage.getItem(BOOK_CACHE_KEY) || "[]");
  } catch (_) {
    return [];
  }
};

const getDownloadedIds = async () => {
  try {
    return JSON.parse(localStorage.getItem(DOWNLOADED_IDS_KEY) || "[]");
  } catch (_) {
    return [];
  }
};

const addDownloadedId = async (bookId) => {
  const ids = await getDownloadedIds();
  const next = Array.from(new Set([...ids.map(String), String(bookId)]));
  localStorage.setItem(DOWNLOADED_IDS_KEY, JSON.stringify(next));
};

const pdfCacheRequest = (bookId) =>
  new Request(`${window.location.origin}/__offline-books/${bookId}.pdf`);

const saveBookFile = async (bookId, blob) => {
  if (!("caches" in window)) return;
  const cache = await window.caches.open(PDF_CACHE_NAME);
  await cache.put(
    pdfCacheRequest(bookId),
    new Response(blob, { headers: { "Content-Type": "application/pdf" } }),
  );
};

const getBookFile = async (bookId) => {
  if (!("caches" in window)) return null;
  const cache = await window.caches.open(PDF_CACHE_NAME);
  const response = await cache.match(pdfCacheRequest(bookId));
  if (!response) return null;

  const bytes = new Uint8Array(await response.arrayBuffer());
  const chunkSize = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(
      ...bytes.subarray(offset, Math.min(offset + chunkSize, bytes.length)),
    );
  }
  return window.btoa(binary);
};

const isBookDownloaded = async (bookId) => {
  const ids = await getDownloadedIds();
  return ids.map(String).includes(String(bookId));
};

const sendSyncQueue = async () => undefined;

const INITIAL_PAGINATION = {
  total: 0,
  page: 1,
  limit: BOOKS_PER_PAGE,
  pages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
  nextPage: null,
  previousPage: null,
  feedSeed: null,
};

const createFeedSeed = () => {
  const webCrypto = typeof window !== "undefined" ? window.crypto : null;

  if (webCrypto?.getRandomValues) {
    const values = new Uint32Array(4);
    webCrypto.getRandomValues(values);
    return Array.from(values, (value) => value.toString(36)).join("-");
  }

  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
};

const formatPrice = (price, currency = "SLE") => {
  const safeCurrency = String(currency || "SLE").toUpperCase();
  if (price == null) return `${safeCurrency} 0.00`;
  const num = typeof price === "string" ? parseFloat(price) : price;
  if (isNaN(num)) return `${safeCurrency} 0.00`;
  return `${safeCurrency} ${num.toFixed(2)}`;
};

// Helper to get the access token from localStorage
const getAccessToken = () => {
  const storedToken =
    localStorage.getItem("accessToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken");
  const axiosAuthorization = apiClient.defaults.headers.common.Authorization;
  const token = storedToken || axiosAuthorization || null;

  return token ? String(token).replace(/^Bearer\s+/i, "") : null;
};

// Read a PDF response in both normal browsers and Capacitor WebViews.
// Some Android WebViews do not expose response.body/getReader(), so requiring
// streaming makes otherwise valid PDFs fail to preview and download.
const readPdfResponse = async (response, onProgress) => {
  const contentType = String(response.headers.get("content-type") || "")
    .toLowerCase();

  if (contentType.includes("application/json")) {
    let payload = null;
    try {
      payload = await response.json();
    } catch (_) {}
    throw new Error(payload?.message || "The server did not return a PDF.");
  }

  if (!response.body || typeof response.body.getReader !== "function") {
    const fallbackBlob = await response.blob();
    if (!fallbackBlob.size) throw new Error("The downloaded PDF was empty.");
    onProgress?.(100);
    return fallbackBlob.type === "application/pdf"
      ? fallbackBlob
      : new Blob([fallbackBlob], { type: "application/pdf" });
  }

  const contentLength = Number.parseInt(
    response.headers.get("content-length") || "0",
    10,
  );
  const reader = response.body.getReader();
  const chunks = [];
  let loaded = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;

    if (contentLength > 0) {
      onProgress?.(Math.min(99, Math.round((loaded * 100) / contentLength)));
    } else {
      onProgress?.(null);
    }
  }

  const blob = new Blob(chunks, { type: "application/pdf" });
  if (!blob.size) throw new Error("The downloaded PDF was empty.");
  onProgress?.(100);
  return blob;
};

const getServerClockOffset = (serverTime) => {
  const parsed = Date.parse(serverTime || "");
  return Number.isFinite(parsed) ? parsed - Date.now() : 0;
};

const formatPaymentCountdown = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "--:--";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(
    remainingSeconds,
  ).padStart(2, "0")}`;
};

const readActiveBookPayment = () => {
  try {
    const value = localStorage.getItem(ACTIVE_BOOK_PAYMENT_KEY);
    return value ? JSON.parse(value) : null;
  } catch (_) {
    return null;
  }
};

const saveActiveBookPayment = (payment) => {
  try {
    localStorage.setItem(ACTIVE_BOOK_PAYMENT_KEY, JSON.stringify(payment));
  } catch (_) {
    // Payment recovery still works from the backend database.
  }
};

const clearActiveBookPayment = () => {
  try {
    localStorage.removeItem(ACTIVE_BOOK_PAYMENT_KEY);
  } catch (_) {
    // Ignore unavailable browser storage.
  }
};

const readDismissedBookPayment = () => {
  try {
    const value = localStorage.getItem(DISMISSED_BOOK_PAYMENT_KEY);
    return value ? JSON.parse(value) : null;
  } catch (_) {
    return null;
  }
};

const saveDismissedBookPayment = (payment) => {
  const purchaseId = payment?.purchaseId || payment?.purchase_id || null;
  const paymentReference =
    payment?.paymentReference ||
    payment?.payment_reference ||
    payment?.paymentCodeId ||
    null;

  if (!purchaseId && !paymentReference) return;

  try {
    localStorage.setItem(
      DISMISSED_BOOK_PAYMENT_KEY,
      JSON.stringify({
        purchaseId,
        paymentReference,
        bookId: payment.bookId || payment.book_id || null,
        dismissedAt: new Date().toISOString(),
      }),
    );
  } catch (_) {
    // The modal still closes when browser storage is unavailable.
  }
};

const clearDismissedBookPayment = () => {
  try {
    localStorage.removeItem(DISMISSED_BOOK_PAYMENT_KEY);
  } catch (_) {
    // Ignore unavailable browser storage.
  }
};

const isDismissedBookPayment = (payment, dismissedPayment) => {
  if (!payment || !dismissedPayment) return false;

  const purchaseId = payment.purchaseId || payment.purchase_id || null;
  const paymentReference =
    payment.paymentReference ||
    payment.payment_reference ||
    payment.paymentCodeId ||
    null;
  const purchaseIdsMatch =
    purchaseId &&
    dismissedPayment.purchaseId &&
    String(purchaseId) === String(dismissedPayment.purchaseId);
  const referencesMatch =
    paymentReference &&
    dismissedPayment.paymentReference &&
    String(paymentReference) === String(dismissedPayment.paymentReference);

  return Boolean(purchaseIdsMatch || referencesMatch);
};

const normalizeBook = (book) => {
  const coverFile = book.cover_image
    ? String(book.cover_image).split("/").pop()
    : null;
  const coverUrl = coverFile
    ? `${API_BASE_URL}/covers/${encodeURIComponent(coverFile)}`
    : null;
  const coverVersion = book.version || book.updated_at;

  return {
    ...book,
    cover:
      book.cover ||
      (coverUrl && coverVersion
        ? `${coverUrl}?v=${encodeURIComponent(coverVersion)}`
        : coverUrl),
  };
};

const normalizeClientSearch = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const BookCardSkeleton = ({ index = 0 }) => (
  <div
    className="rounded-xl border border-gray-100 overflow-hidden bg-white"
    style={{ animationDelay: `${index * 35}ms` }}
    aria-hidden="true"
  >
    <div className="book-skeleton-shimmer bg-gray-200 animate-pulse h-36 sm:h-44 md:h-48" />
    <div className="p-3 sm:p-4 space-y-2.5">
      <div className="book-skeleton-shimmer bg-gray-200 animate-pulse h-4 sm:h-5 rounded-md w-4/5" />
      <div className="book-skeleton-shimmer bg-gray-200 animate-pulse h-3.5 rounded-md w-3/5" />
      <div className="flex items-center justify-between pt-1">
        <div className="book-skeleton-shimmer bg-gray-200 animate-pulse h-5 rounded-md w-16" />
        <div className="book-skeleton-shimmer bg-gray-200 animate-pulse h-5 rounded-md w-12" />
      </div>
    </div>
  </div>
);

// Keep keystrokes local to this small component. The large Library page only
// re-renders after the 350ms debounce, so typing remains as smooth as Landing.
const LibrarySearch = React.memo(function LibrarySearch({
  onDebouncedSearch,
  resetKey,
}) {
  const [value, setValue] = useState("");

  useEffect(() => {
    setValue("");
  }, [resetKey]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      onDebouncedSearch(value.trim());
    }, 350);

    return () => window.clearTimeout(timer);
  }, [onDebouncedSearch, value]);

  return (
    <div className="relative flex-1">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 sm:h-5 sm:w-5" />
      <input
        type="search"
        placeholder="Search by title, author, subject or description..."
        value={value}
        onChange={(event) => setValue(event.target.value)}
        className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100 sm:py-2.5 sm:pl-10 sm:pr-4 sm:text-base"
      />
    </div>
  );
});

const InitialLibrarySkeleton = () => (
  <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-2 sm:pt-3 md:pt-4 pb-6">
    <div
      className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-5"
      aria-hidden="true"
    >
      <div className="h-10 flex-1 rounded-lg bg-gray-200 animate-pulse" />
      <div className="flex gap-2 overflow-hidden">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={`filter-skeleton-${index}`}
            className="h-9 w-20 rounded-md bg-gray-200 animate-pulse flex-shrink-0"
          />
        ))}
      </div>
    </div>

    <div
      className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5"
      aria-label="Loading books"
      aria-busy="true"
    >
      {Array.from({ length: INITIAL_SKELETON_COUNT }).map((_, index) => (
        <BookCardSkeleton
          key={`initial-book-skeleton-${index}`}
          index={index}
        />
      ))}
    </div>
  </div>
);

const BookCover = React.memo(function BookCover({ book, index }) {
  const [imageStatus, setImageStatus] = useState(
    book.cover ? "loading" : "empty",
  );

  const hasUsableCover = book.cover && imageStatus !== "error";
  const isLoaded = imageStatus === "loaded";
  const shouldLoadEagerly = index < EAGER_COVER_COUNT;
  const isHighPriority = index < HIGH_PRIORITY_COVER_COUNT;

  if (!hasUsableCover) {
    return (
      <div className="flex flex-col items-center" aria-hidden="true">
        <BookOpenIcon className="w-14 sm:w-20 h-14 sm:h-20 text-gray-300 group-hover:text-primary-300 transition-colors" />
      </div>
    );
  }

  return (
    <>
      {!isLoaded && (
        <div
          className="book-skeleton-shimmer absolute inset-0 flex items-center justify-center"
          aria-hidden="true"
        >
          <BookOpenIcon className="w-12 sm:w-16 h-12 sm:h-16 text-gray-300/70" />
        </div>
      )}
      <img
        src={book.cover}
        alt={book.title}
        width="320"
        height="240"
        className={`relative h-full w-full object-contain bg-white transition-opacity duration-300 ease-out ${
          isLoaded ? "opacity-100" : "opacity-0"
        }`}
        loading={shouldLoadEagerly ? "eager" : "lazy"}
        fetchPriority={isHighPriority ? "high" : "auto"}
        decoding="async"
        draggable="false"
        onLoad={() => setImageStatus("loaded")}
        onError={() => setImageStatus("error")}
      />
    </>
  );
});

export default function Library() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [filterSector, setFilterSector] = useState("All");
  const [selectedBook, setSelectedBook] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [pdfError, setPdfError] = useState(false);

  // Favorites
  const [favorites, setFavorites] = useState(() => {
    const stored = localStorage.getItem("favorites");
    return stored ? JSON.parse(stored) : [];
  });

  // Purchased IDs (cached)
  const [purchasedIds, setPurchasedIds] = useState(new Set());
  // Download tracking
  const [downloadedIds, setDownloadedIds] = useState([]);
  const [downloadStates, setDownloadStates] = useState({});
  // Purchase flow
  const [premiumBook, setPremiumBook] = useState(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [isSheetVisible, setIsSheetVisible] = useState(false);
  const [purchaseStep, setPurchaseStep] = useState("init"); // init | pay
  const [paymentRef, setPaymentRef] = useState("");
  const [paymentUssdCode, setPaymentUssdCode] = useState("");
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [paymentCurrency, setPaymentCurrency] = useState("SLE");
  const [paymentExpiresAt, setPaymentExpiresAt] = useState(null);
  const [currentPurchaseId, setCurrentPurchaseId] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [autoCheckingPayment, setAutoCheckingPayment] = useState(false);
  const [paymentCheckMessage, setPaymentCheckMessage] = useState("");
  const [paymentSecondsRemaining, setPaymentSecondsRemaining] = useState(null);
  const [paymentServerOffsetMs, setPaymentServerOffsetMs] = useState(0);

  const blobUrlRef = useRef(null);
  const booksRef = useRef([]);
  // One seed is used for the whole scrolling session. A new component view
  // creates a new seed and therefore a newly mixed social-style book feed.
  const feedSeedRef = useRef(createFeedSeed());
  const fetchBooksRef = useRef();
  const abortControllerRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);
  const booksRequestInFlightRef = useRef(false);
  const activeBooksRequestKeyRef = useRef(null);
  const queuedBooksRequestRef = useRef(null);
  const paymentStatusCheckInFlightRef = useRef(false);
  const providerVerificationInFlightRef = useRef(false);
  const lastProviderCheckAtRef = useRef(0);
  const successfulPaymentHandledRef = useRef(false);
  const paymentModalDismissedRef = useRef(false);
  const openPdfRef = useRef(null);
  const closePremiumRef = useRef(null);
  const pendingPaymentRecoveryStartedRef = useRef(false);
  const downloadControllersRef = useRef(new Map());
  const lastDownloadProgressRef = useRef(new Map());

  // Online status
  const isOnline = useOnlineStatus();

  const handleDebouncedSearch = useCallback((nextSearch) => {
    if (nextSearch) setFilterSector("All");
    setSearch(nextSearch);
  }, []);

  // Custom toolbar that removes download and print slots
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar: (Toolbar) => (props) => (
      <Toolbar {...props}>
        {(slots) => {
          // Exclude download and print slots
          const { download, print, ...restSlots } = slots;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {Object.values(restSlots).map((slot, index) => (
                <React.Fragment key={index}>{slot}</React.Fragment>
              ))}
            </div>
          );
        }}
      </Toolbar>
    ),
  });

  // Fetch one page of books while preserving the offline cache.
  const fetchBooks = useCallback(
    async ({ page = 1, append = false, background = false } = {}) => {
      const requestKey = `${isOnline ? "online" : "offline"}:${
        append ? "append" : "replace"
      }:${page}:${feedSeedRef.current}:${search}:${filterSector}`;

      // The bottom observer can fire repeatedly while it remains visible. This
      // prevents duplicate requests and duplicate book cards. React StrictMode
      // can also run the initial effect twice in development, so an identical
      // startup request is ignored instead of being queued and replayed.
      if (booksRequestInFlightRef.current && append) {
        return;
      }

      if (booksRequestInFlightRef.current && !append) {
        // Always replay the latest replace request after cancelling the old
        // one. React StrictMode mounts, cleans up and mounts effects again in
        // development. The second initial request has the same key, but it
        // must still be queued or the catalogue request is cancelled before
        // it ever reaches the network.
        queuedBooksRequestRef.current = { page, append, background };
        abortControllerRef.current?.abort();
        return;
      }

      booksRequestInFlightRef.current = true;
      activeBooksRequestKeyRef.current = requestKey;
      const controller = new AbortController();
      abortControllerRef.current = controller;
      let cacheFallbackShown = false;
      const shouldShowPageLoader = !append && !background;

      try {
        if (append) {
          setLoadingMore(true);
          setLoadMoreError(null);
        } else if (shouldShowPageLoader) {
          setLoading(true);
        }

        // If offline, try to load from cache
        if (!isOnline) {
          const cached = booksRef.current.length
            ? booksRef.current
            : ((await getCachedBooks()) || []).map(normalizeBook);
          if (cached && cached.length > 0) {
            booksRef.current = cached;
            setBooks(cached);
            setPagination({
              ...INITIAL_PAGINATION,
              total: cached.length,
              limit: cached.length || BOOKS_PER_PAGE,
              feedSeed: feedSeedRef.current,
            });
            setError(null);
            return;
          } else {
            throw new Error("No cached books available offline");
          }
        }

        // Use the authenticated school catalogue that already works for
        // students. apiClient owns authentication through its interceptor;
        // do not reject a valid AuthContext session based on storage keys.
        const response = await apiClient.get("/books", {
          params: {
            page,
            limit: BOOKS_PER_PAGE,
            feedSeed: feedSeedRef.current,
            ...(search ? { search } : {}),
            ...(!search &&
            filterSector !== "All" &&
            filterSector !== "Favorites" &&
            filterSector !== "Premium"
              ? { sector: filterSector }
              : {}),
          },
          signal: controller.signal,
        });

        if (response.data.success) {
          const mappedBooks = response.data.data.map(normalizeBook);

          let nextBooks = mappedBooks;

          if (append) {
            const existingIds = new Set(
              booksRef.current.map((book) => String(book.id)),
            );
            const uniqueNewBooks = mappedBooks.filter(
              (book) => !existingIds.has(String(book.id)),
            );
            nextBooks = [...booksRef.current, ...uniqueNewBooks];
          }

          booksRef.current = nextBooks;
          setBooks(nextBooks);

          // Cache every page loaded so the same collection is available offline.
          // A device storage problem must not break online pagination.
          try {
            await cacheBooks(nextBooks);
          } catch (cacheError) {
            console.warn("Books cache update failed:", cacheError.message);
          }

          const serverPagination = response.data.pagination || {};
          const responseFeedSeed =
            serverPagination.feedSeed || feedSeedRef.current;
          feedSeedRef.current = responseFeedSeed;
          const currentPage = Number(serverPagination.page) || page;
          const total = Number(serverPagination.total) || 0;
          const pages = Number(serverPagination.pages) || 0;
          const hasNextPage =
            typeof serverPagination.hasNextPage === "boolean"
              ? serverPagination.hasNextPage
              : currentPage < pages;

          setPagination({
            total,
            page: currentPage,
            limit: Number(serverPagination.limit) || BOOKS_PER_PAGE,
            pages,
            hasNextPage,
            hasPreviousPage:
              typeof serverPagination.hasPreviousPage === "boolean"
                ? serverPagination.hasPreviousPage
                : currentPage > 1,
            nextPage: hasNextPage
              ? Number(serverPagination.nextPage) || currentPage + 1
              : null,
            previousPage:
              currentPage > 1
                ? Number(serverPagination.previousPage) || currentPage - 1
                : null,
            feedSeed: responseFeedSeed,
          });

          setError(null);
          setLoadMoreError(null);
        } else {
          throw new Error(response.data.message || "Unknown error");
        }
      } catch (err) {
        if (err.name === "CanceledError" || err.name === "AbortError") return;

        const message =
          err.response?.data?.message || err.message || "Failed to fetch books";

        if (append) {
          // Keep the books already on screen and show a small retry action below
          // the grid instead of replacing the entire page with an error.
          setLoadMoreError(message);
        } else if (booksRef.current.length > 0) {
          // A background refresh must never replace usable cards with a
          // full-page error. The next online refresh can try again quietly.
          setError(null);
          console.warn("Books background refresh failed:", message);
        } else {
          // Do not show stale cached ordering before a normal online response.
          // Cache is used here only as a recovery path after that request fails.
          try {
            const cached = await getCachedBooks();
            if (Array.isArray(cached) && cached.length > 0) {
              const normalizedCachedBooks = cached.map(normalizeBook);
              booksRef.current = normalizedCachedBooks;
              setBooks(normalizedCachedBooks);
              setPagination({
                ...INITIAL_PAGINATION,
                total: normalizedCachedBooks.length,
                limit: normalizedCachedBooks.length || BOOKS_PER_PAGE,
                feedSeed: feedSeedRef.current,
              });
              setError(null);
              cacheFallbackShown = true;
              console.warn(
                "Showing cached books after live request failed:",
                message,
              );
            }
          } catch (cacheError) {
            console.debug(
              "Books cache fallback unavailable:",
              cacheError.message,
            );
          }

          if (
            !cacheFallbackShown &&
            message.includes("not associated with any school")
          ) {
            setError(
              "You need a school account to access the library. Please contact your school administrator.",
            );
          } else if (!cacheFallbackShown) {
            setError(message);
          }
        }

        if (
          !append &&
          shouldShowPageLoader &&
          booksRef.current.length === 0 &&
          !cacheFallbackShown &&
          !message.includes("not associated with any school")
        ) {
          toast.error(message);
        }
      } finally {
        if (abortControllerRef.current !== controller) return;

        abortControllerRef.current = null;
        booksRequestInFlightRef.current = false;
        activeBooksRequestKeyRef.current = null;

        if (append) {
          setLoadingMore(false);
        } else if (shouldShowPageLoader) {
          setLoading(false);
        }

        const queuedRequest = queuedBooksRequestRef.current;
        if (queuedRequest) {
          queuedBooksRequestRef.current = null;
          Promise.resolve().then(() => {
            fetchBooksRef.current?.(queuedRequest);
          });
        }
      }
    },
    [filterSector, isOnline, search],
  );

  const loadNextPage = useCallback(() => {
    if (
      !isOnline ||
      loading ||
      loadingMore ||
      booksRequestInFlightRef.current ||
      !pagination.hasNextPage
    ) {
      return;
    }

    const nextPage = pagination.nextPage || pagination.page + 1;
    fetchBooks({ page: nextPage, append: true });
  }, [fetchBooks, isOnline, loading, loadingMore, pagination]);

  useEffect(() => {
    fetchBooksRef.current = fetchBooks;
  }, [fetchBooks]);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    fetchBooks({
      page: 1,
      append: false,
      background: booksRef.current.length > 0,
    });
  }, [fetchBooks]);

  // Load the next 20 books shortly before the user reaches the bottom.
  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;

    if (
      !trigger ||
      !isOnline ||
      !pagination.hasNextPage ||
      loadMoreError ||
      typeof IntersectionObserver === "undefined"
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadNextPage();
        }
      },
      {
        root: null,
        rootMargin: "650px 0px",
        threshold: 0.01,
      },
    );

    observer.observe(trigger);

    return () => observer.disconnect();
  }, [isOnline, loadNextPage, loadMoreError, pagination.hasNextPage]);

  // Load downloaded IDs
  useEffect(() => {
    const loadDownloaded = async () => {
      const ids = await getDownloadedIds();
      setDownloadedIds(ids);
    };
    loadDownloaded();
  }, []);

  // Cancel only genuine in-flight transfers when the page unmounts. Each
  // book has its own controller, so different books can download in parallel.
  useEffect(() => {
    const controllers = downloadControllersRef.current;

    return () => {
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
      lastDownloadProgressRef.current.clear();
    };
  }, []);

  // Fetch purchased books (only if online and authenticated)
  useEffect(() => {
    let cancelled = false;

    const fetchPurchased = async () => {
      if (!isOnline) return;

      try {
        const res = await apiClient.get("/books/purchased");
        if (!cancelled && res.data.success) {
          const purchasedBooks = Array.isArray(res.data.data)
            ? res.data.data
            : [];
          const ids = new Set(
            purchasedBooks
              .map((book) => book?.id)
              .filter((id) => id !== undefined && id !== null)
              .map((id) => String(id)),
          );
          // The pending-payment recovery request can finish at the same time
          // as this list request. Merge instead of replacing so an older,
          // empty response cannot erase a book that was just confirmed.
          setPurchasedIds((previous) => {
            const next = new Set(previous);
            ids.forEach((id) => next.add(id));
            return next;
          });
        }
      } catch (err) {
        console.debug("Purchased books fetch skipped or failed:", err.message);
      }
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        fetchPurchased();
      }
    };

    fetchPurchased();

    // Refresh ownership when the user returns from the phone dialer or
    // switches back to the app after a webhook completed the payment.
    window.addEventListener("focus", fetchPurchased);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", fetchPurchased);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [isOnline]);

  // Restore an unfinished payment after an app refresh, phone restart, or
  // backend restart. Local storage makes the UI appear immediately, while
  // the backend remains the trusted source of payment status.
  useEffect(() => {
    if (!isOnline || pendingPaymentRecoveryStartedRef.current) {
      return undefined;
    }

    pendingPaymentRecoveryStartedRef.current = true;
    let cancelled = false;
    let retryTimer = null;

    const dismissRestoredPayment = () => {
      setAutoCheckingPayment(false);
      setPaymentCheckMessage("");
      setIsSheetVisible(false);
      setShowPremiumModal(false);
      setPremiumBook(null);
      setPurchaseStep("init");
      setPaymentRef("");
      setPaymentUssdCode("");
      setPaymentAmount(null);
      setPaymentExpiresAt(null);
      setCurrentPurchaseId(null);
      setPaymentSecondsRemaining(null);
      setPaymentServerOffsetMs(0);
    };

    const showRestoredPayment = (payment, fallbackBook = null) => {
      if (cancelled || !payment?.purchaseId || !payment?.bookId) {
        return;
      }

      const book = booksRef.current.find(
        (item) => String(item.id) === String(payment.bookId),
      ) ||
        fallbackBook ||
        payment.book || {
          id: payment.bookId,
          title: "Premium Book",
          author: "",
          premium: true,
          price: payment.amount,
          currency: payment.currency || "SLE",
        };

      setPremiumBook(book);
      setPaymentRef(payment.paymentReference || payment.paymentCodeId || "");
      setPaymentUssdCode(payment.ussdCode || "");
      setPaymentAmount(payment.amount ?? book.price ?? null);
      setPaymentCurrency(payment.currency || book.currency || "SLE");
      setPaymentExpiresAt(payment.expiresAt || null);
      setPaymentServerOffsetMs(
        Number.isFinite(payment.serverClockOffsetMs)
          ? payment.serverClockOffsetMs
          : getServerClockOffset(payment.serverTime),
      );
      setCurrentPurchaseId(payment.purchaseId);
      setPurchaseStep("pay");
      setPaymentCheckMessage(
        "Restored your payment. We are checking it automatically.",
      );
      setAutoCheckingPayment(true);
      successfulPaymentHandledRef.current = false;
      paymentModalDismissedRef.current = false;
      setShowPremiumModal(true);
      requestAnimationFrame(() => setIsSheetVisible(true));
    };

    const dismissedPayment = readDismissedBookPayment();
    const cachedPayment = readActiveBookPayment();
    if (cachedPayment?.purchaseId && cachedPayment?.bookId) {
      const offset = Number.isFinite(cachedPayment.serverClockOffsetMs)
        ? cachedPayment.serverClockOffsetMs
        : 0;
      const expiresAt = Date.parse(cachedPayment.expiresAt || "");

      if (isDismissedBookPayment(cachedPayment, dismissedPayment)) {
        // The user explicitly closed this payment. Keep the pending record in
        // the backend, but never reopen its modal on refresh.
        clearActiveBookPayment();
      } else if (
        !Number.isFinite(expiresAt) ||
        Date.now() + offset < expiresAt
      ) {
        showRestoredPayment(cachedPayment);
      } else {
        clearActiveBookPayment();
        clearDismissedBookPayment();
      }
    }

    const recoverFromBackend = async () => {
      try {
        const response = await apiClient.get("/books/purchase/pending");

        if (cancelled) return;

        if (response.data?.purchased === true) {
          const bookId = response.data?.data?.bookId;
          if (bookId) {
            setPurchasedIds((previous) => {
              const next = new Set(previous);
              next.add(String(bookId));
              return next;
            });
          }

          clearActiveBookPayment();
          clearDismissedBookPayment();
          dismissRestoredPayment();
          toast.success("Your previous payment was confirmed.");
          return;
        }

        const payment = response.data?.data;

        if (payment?.purchaseId && payment?.bookId) {
          const serverClockOffsetMs = getServerClockOffset(payment.serverTime);
          const restoredPayment = {
            ...payment,
            serverClockOffsetMs,
            book: payment.book || null,
          };

          if (
            isDismissedBookPayment(restoredPayment, readDismissedBookPayment())
          ) {
            clearActiveBookPayment();
            dismissRestoredPayment();
            return;
          }

          saveActiveBookPayment(restoredPayment);
          showRestoredPayment(restoredPayment, payment.book || null);
          return;
        }

        if (response.data?.expired === true || !payment) {
          clearActiveBookPayment();
          clearDismissedBookPayment();
          dismissRestoredPayment();
        }
      } catch (error) {
        if (cancelled || error.response?.status === 401) return;

        // The server may still be restarting. Keep any cached payment on
        // screen and retry recovery without requiring user interaction.
        retryTimer = setTimeout(recoverFromBackend, 15000);
      }
    };

    recoverFromBackend();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [isOnline]);

  // Favorites persistence
  useEffect(() => {
    localStorage.setItem("favorites", JSON.stringify(favorites));
  }, [favorites]);

  // Sync queue when online
  useEffect(() => {
    if (isOnline) {
      sendSyncQueue(apiClient).catch(console.error);
    }
  }, [isOnline]);

  const toggleFavorite = (bookId, e) => {
    e.stopPropagation();
    setFavorites((prev) =>
      prev.includes(bookId)
        ? prev.filter((id) => id !== bookId)
        : [...prev, bookId],
    );
  };

  // Download progress belongs to each individual book button. Progress state
  // is updated in 5% steps to avoid rerendering the whole grid for every
  // network chunk, while separate controllers allow parallel downloads.
  const downloadBook = useCallback(
    async (book) => {
      const bookKey = String(book.id);

      if (downloadControllersRef.current.has(bookKey)) return;

      if (!isOnline) {
        setDownloadStates((previous) => ({
          ...previous,
          [bookKey]: {
            status: "error",
            progress: 0,
            message: "Connect to the internet and try again.",
          },
        }));
        return;
      }

      const token = getAccessToken();

      // IndexedDB is authoritative for offline availability. This also blocks
      // a duplicate transfer if the ID list has not finished loading yet.
      let alreadyDownloaded = false;
      try {
        alreadyDownloaded = await isBookDownloaded(book.id);
      } catch (storageError) {
        console.warn("Offline download lookup failed:", storageError);
      }

      if (alreadyDownloaded) {
        setDownloadedIds((previous) =>
          previous.some((id) => String(id) === bookKey)
            ? previous
            : [...previous, book.id],
        );
        setDownloadStates((previous) => ({
          ...previous,
          [bookKey]: { status: "completed", progress: 100 },
        }));
        return;
      }

      const controller = new AbortController();
      downloadControllersRef.current.set(bookKey, controller);
      lastDownloadProgressRef.current.set(bookKey, -5);
      setDownloadStates((previous) => ({
        ...previous,
        [bookKey]: { status: "downloading", progress: 0 },
      }));

      try {
        const baseUrl = String(
          apiClient.defaults.baseURL || API_URL,
        ).replace(/\/+$/, "");
        const url = `${baseUrl}/books/${book.id}/download?t=${Date.now()}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: "application/pdf",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });

        if (!response.ok) {
          let errorMessage = `Download failed (${response.status})`;

          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch (_) {
            // The endpoint may return a non-JSON error response.
          }

          throw new Error(errorMessage);
        }

        const blob = await readPdfResponse(response, (rawProgress) => {
          const displayedProgress = Number.isFinite(rawProgress)
            ? Math.floor(rawProgress / 5) * 5
            : null;
          const previousProgress =
            lastDownloadProgressRef.current.get(bookKey);

          if (
            displayedProgress === null ||
            previousProgress === undefined ||
            displayedProgress > previousProgress
          ) {
            lastDownloadProgressRef.current.set(bookKey, displayedProgress);
            setDownloadStates((previous) => ({
              ...previous,
              [bookKey]: {
                status: "downloading",
                progress: displayedProgress,
              },
            }));
          }
        });

        await saveBookFile(book.id, blob);

        // Keep the storage index synchronized when this helper is available.
        try {
          await addDownloadedId(book.id);
        } catch (storageError) {
          console.warn("Downloaded ID index update failed:", storageError);
        }

        setDownloadedIds((previous) =>
          previous.some((id) => String(id) === bookKey)
            ? previous
            : [...previous, book.id],
        );
        setDownloadStates((previous) => ({
          ...previous,
          [bookKey]: { status: "completed", progress: 100 },
        }));
        // Intentionally no completion toast: the green disabled button is the
        // persistent success feedback.
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Download error:", error);
          setDownloadStates((previous) => ({
            ...previous,
            [bookKey]: {
              status: "error",
              progress: 0,
              message: error.message || "Download failed. Tap to retry.",
            },
          }));
        }
      } finally {
        downloadControllersRef.current.delete(bookKey);
        lastDownloadProgressRef.current.delete(bookKey);
      }
    },
    [isOnline],
  );

  // --- Open PDF (from local or online) - using fetch ---
  const openPdf = async (book) => {
    // Cancel any ongoing download
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setSelectedBook({ ...book, pdfUrl: null });
    setPdfLoading(true);
    setPdfProgress(0);
    setPdfError(false);

    try {
      let fileData = null;
      // Check if already downloaded
      const downloaded = await isBookDownloaded(book.id);
      if (downloaded) {
        // Read from local storage
        const base64 = await getBookFile(book.id);
        if (base64) {
          // Convert base64 to blob
          const binary = atob(base64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: "application/pdf" });
          fileData = URL.createObjectURL(blob);
        }
      }

      if (!fileData) {
        // Not downloaded, fetch from API (requires internet)
        if (!isOnline) {
          toast.error(
            "You need to download this book first for offline reading.",
          );
          setPdfLoading(false);
          setPdfError(true);
          return;
        }

        const token = getAccessToken();

        const baseUrl = String(
          apiClient.defaults.baseURL || API_URL,
        ).replace(/\/+$/, "");
        const url = `${baseUrl}/books/${book.id}/download?t=${Date.now()}`;

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: "application/pdf",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });

        if (!response.ok) {
          let errorMsg = `Server error: ${response.status}`;
          try {
            const errorData = await response.json();
            if (errorData.message) errorMsg = errorData.message;
            else if (errorData.error) errorMsg = errorData.error;
          } catch (_) {}
          throw new Error(errorMsg);
        }

        const blob = await readPdfResponse(response, (progress) => {
          if (Number.isFinite(progress)) setPdfProgress(progress);
          else setPdfProgress((previous) => Math.min(previous + 5, 90));
        });
        fileData = URL.createObjectURL(blob);
        abortControllerRef.current = null;
      }

      blobUrlRef.current = fileData;
      setSelectedBook({ ...book, pdfUrl: fileData });
      setPdfProgress(100);
    } catch (err) {
      console.error("PDF open error:", err);
      setPdfLoading(false);
      setPdfError(true);
      if (err.name === "AbortError") {
        // Do not show toast for cancellation
      } else {
        toast.error(err.message || "Failed to open PDF.");
      }
    } finally {
      abortControllerRef.current = null;
    }
  };

  // Handle book click
  const handleBookClick = async (book) => {
    if (book.premium) {
      if (purchasedIds.has(String(book.id))) {
        openPdf(book);
        return;
      }
      // Not purchased – show purchase modal
      setPremiumBook(book);
      setPurchaseStep("init");
      setPaymentRef("");
      setPaymentUssdCode("");
      setPaymentAmount(null);
      setPaymentCurrency(book.currency || "SLE");
      setPaymentExpiresAt(null);
      setCurrentPurchaseId(null);
      setPaymentSecondsRemaining(null);
      setPaymentServerOffsetMs(0);
      setAutoCheckingPayment(false);
      setPaymentCheckMessage("");
      successfulPaymentHandledRef.current = false;
      paymentModalDismissedRef.current = false;
      setShowPremiumModal(true);
      requestAnimationFrame(() => setIsSheetVisible(true));
      return;
    }
    // Free book
    openPdf(book);
  };

  // Purchase functions
  const handleBuyNow = async () => {
    if (!premiumBook) return;

    if (!isOnline) {
      toast.error("You need internet to purchase.");
      return;
    }

    paymentModalDismissedRef.current = false;
    setPurchaseLoading(true);

    try {
      const res = await apiClient.post(
        "/books/purchase",
        {
          // The backend gets userId from the JWT and gets title/price from DB.
          bookId: premiumBook.id,
        },
        {},
      );

      if (!res.data?.success) {
        throw new Error(res.data?.message || "Purchase initiation failed");
      }

      if (res.data.purchased === true) {
        clearActiveBookPayment();
        clearDismissedBookPayment();
        setPurchasedIds((previous) => {
          const next = new Set(previous);
          next.add(String(premiumBook.id));
          return next;
        });

        if (!paymentModalDismissedRef.current) {
          toast.success("You already own this book.");
          handleClosePremium();
          openPdf(premiumBook);
        }
        return;
      }

      const payment = res.data?.data;

      if (!payment?.purchaseId || !payment?.ussdCode) {
        throw new Error("The server did not return a valid Monime USSD code.");
      }

      // The user may close the sheet while the network request is still in
      // flight. If that happens, suppress this newly returned payment too.
      if (paymentModalDismissedRef.current) {
        saveDismissedBookPayment({
          purchaseId: payment.purchaseId,
          paymentReference:
            payment.paymentReference || payment.paymentCodeId || null,
          bookId: payment.bookId || premiumBook.id,
        });
        clearActiveBookPayment();
        return;
      }

      setPaymentRef(payment.paymentReference || payment.paymentCodeId || "");
      setPaymentUssdCode(payment.ussdCode);
      setPaymentAmount(payment.amount ?? premiumBook.price);
      setPaymentCurrency(payment.currency || premiumBook.currency || "SLE");
      setPaymentExpiresAt(payment.expiresAt || null);
      setCurrentPurchaseId(payment.purchaseId);
      const serverClockOffsetMs = getServerClockOffset(payment.serverTime);
      setPaymentServerOffsetMs(serverClockOffsetMs);
      setAutoCheckingPayment(true);
      setPaymentCheckMessage(
        "Waiting for payment. We will confirm it automatically.",
      );
      successfulPaymentHandledRef.current = false;
      setPurchaseStep("pay");

      // Reopening the purchase flow and generating/receiving a code is an
      // explicit user action, so this payment may be shown again normally.
      clearDismissedBookPayment();
      saveActiveBookPayment({
        ...payment,
        bookId: payment.bookId || premiumBook.id,
        serverClockOffsetMs,
        book: {
          id: premiumBook.id,
          title: premiumBook.title,
          author: premiumBook.author,
          premium: true,
          price: premiumBook.price,
          currency: premiumBook.currency || "SLE",
        },
      });

      toast.success(
        `Your payment code is ready for ${formatPrice(
          payment.amount ?? premiumBook.price,
          payment.currency || premiumBook.currency || "SLE",
        )}. Tap Pay Now to open your phone dialer.`,
        { duration: 6000 },
      );
    } catch (err) {
      const status = err.response?.status;
      const responseData = err.response?.data;
      const message =
        responseData?.message ||
        err.message ||
        "Unable to generate the payment code.";

      if (
        paymentModalDismissedRef.current &&
        !(status === 409 && responseData?.purchased === true)
      ) {
        return;
      }

      if (status === 401) {
        toast.error("Your login session has expired. Please log in again.");
      } else if (status === 409 && responseData?.purchased === true) {
        clearActiveBookPayment();
        clearDismissedBookPayment();
        setPurchasedIds((previous) => {
          const next = new Set(previous);
          next.add(String(premiumBook.id));
          return next;
        });

        if (!paymentModalDismissedRef.current) {
          toast.success("You already own this book.");
          handleClosePremium();
          openPdf(premiumBook);
        }
      } else {
        toast.error(message);
      }
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!currentPurchaseId && !paymentRef) {
      toast.error("No purchase to confirm");
      return;
    }

    if (!isOnline) {
      toast.error("You need internet to confirm payment.");
      return;
    }

    setPurchaseLoading(true);

    try {
      const payload = currentPurchaseId
        ? { purchase_id: currentPurchaseId }
        : { payment_reference: paymentRef };

      const res = await apiClient.post("/books/purchase/confirm", payload);

      // A 202 response can have success=true while payment is still pending.
      // Unlock the book only when the backend explicitly confirms purchase.
      if (res.data?.purchased === true) {
        successfulPaymentHandledRef.current = true;
        clearActiveBookPayment();
        clearDismissedBookPayment();
        setAutoCheckingPayment(false);
        setPaymentCheckMessage("Payment confirmed. Unlocking your book...");
        toast.success("Payment confirmed! Book is now available.");

        setPurchasedIds((previous) => {
          const next = new Set(previous);
          next.add(String(premiumBook.id));
          return next;
        });

        handleClosePremium();
        openPdf(premiumBook);
        return;
      }

      if (res.status === 202 || res.data?.purchased === false) {
        setAutoCheckingPayment(true);
        setPaymentCheckMessage(
          "Waiting for payment confirmation. You can leave this screen open.",
        );
        toast(
          res.data?.message ||
            "Payment is still pending. Complete the USSD payment and try again.",
          {
            icon: "⏳",
            duration: 5000,
          },
        );
        return;
      }

      toast.error(res.data?.message || "Payment could not be confirmed.");
    } catch (err) {
      const status = err.response?.status;
      const responseData = err.response?.data;
      const message =
        responseData?.message || err.message || "Unable to confirm payment.";

      if (status === 401) {
        toast.error("Your login session has expired. Please log in again.");
      } else if (status === 402) {
        clearActiveBookPayment();
        clearDismissedBookPayment();
        toast.error(message);
        setAutoCheckingPayment(false);
        setPaymentCheckMessage("");
        setPurchaseStep("init");
        setPaymentRef("");
        setPaymentUssdCode("");
        setPaymentExpiresAt(null);
        setCurrentPurchaseId(null);
      } else {
        toast.error(message);
      }
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handlePayNow = () => {
    const ussdCode = String(paymentUssdCode || "").trim();

    if (!ussdCode) {
      toast.error("No USSD payment code is available. Please try again.");
      return;
    }

    if (!isOnline) {
      toast.error("You need internet before starting the payment.");
      return;
    }

    // Start the inexpensive backend-status checks before leaving for the
    // dialer. When the user returns, a focus/visibility event immediately
    // asks the backend to verify the payment with Monime.
    successfulPaymentHandledRef.current = false;
    lastProviderCheckAtRef.current = 0;
    setAutoCheckingPayment(true);
    setPaymentCheckMessage(
      "Complete the payment in your dialer. We will check it automatically when you return.",
    );

    // Encoding is important because an unescaped # can be treated as a URL
    // fragment instead of being passed to the phone dialer.
    const dialUrl = `tel:${encodeURIComponent(ussdCode)}`;

    try {
      window.location.assign(dialUrl);
    } catch (error) {
      console.error("Unable to open phone dialer:", error);
      toast.error(
        "Could not open the dialer. Copy and dial the USSD code manually.",
      );
    }
  };

  const dismissCurrentPayment = () => {
    if (
      successfulPaymentHandledRef.current ||
      (!currentPurchaseId && !paymentRef)
    ) {
      return;
    }

    saveDismissedBookPayment({
      purchaseId: currentPurchaseId,
      paymentReference: paymentRef,
      bookId: premiumBook?.id || null,
    });
    clearActiveBookPayment();
  };

  const handleBackFromPayment = () => {
    paymentModalDismissedRef.current = true;
    dismissCurrentPayment();
    setAutoCheckingPayment(false);
    setPaymentCheckMessage("");
    setPurchaseStep("init");
    setPaymentRef("");
    setPaymentUssdCode("");
    setPaymentAmount(null);
    setPaymentCurrency(premiumBook?.currency || "SLE");
    setPaymentExpiresAt(null);
    setCurrentPurchaseId(null);
    setPaymentSecondsRemaining(null);
    setPaymentServerOffsetMs(0);
  };

  const handleClosePremium = () => {
    // Closing a generated payment is an explicit dismissal. Remember it so
    // local cache and the backend pending-payment recovery cannot reopen the
    // same modal after a refresh.
    paymentModalDismissedRef.current = true;

    if (purchaseStep === "pay") {
      dismissCurrentPayment();
    }

    setAutoCheckingPayment(false);
    setPaymentCheckMessage("");
    setIsSheetVisible(false);
    setTimeout(() => {
      setShowPremiumModal(false);
      setPremiumBook(null);
      setPurchaseStep("init");
      setPaymentRef("");
      setPaymentUssdCode("");
      setPaymentAmount(null);
      setPaymentCurrency("SLE");
      setPaymentExpiresAt(null);
      setCurrentPurchaseId(null);
      setPaymentSecondsRemaining(null);
      setPaymentServerOffsetMs(0);
      successfulPaymentHandledRef.current = false;
    }, 300);
  };

  // Keep the long-running payment checker independent from function identity
  // changes on normal component renders.
  openPdfRef.current = openPdf;
  closePremiumRef.current = handleClosePremium;

  // Webhooks are the main completion mechanism. While the payment sheet is
  // open, check our lightweight local endpoint and periodically request a
  // safe Monime verification. This makes confirmation automatic even when a
  // mobile browser does not fire a focus event after closing the dialer.
  useEffect(() => {
    if (
      !autoCheckingPayment ||
      purchaseStep !== "pay" ||
      !premiumBook ||
      !isOnline
    ) {
      return undefined;
    }

    const book = premiumBook;
    const expirationTime = paymentExpiresAt
      ? Date.parse(paymentExpiresAt)
      : NaN;
    let stopped = false;
    let pollTimer = null;
    let providerTimer = null;

    const stopForExpiredCode = (message) => {
      if (stopped) return;
      stopped = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (providerTimer) clearTimeout(providerTimer);
      clearActiveBookPayment();
      clearDismissedBookPayment();
      setAutoCheckingPayment(false);
      setPaymentCheckMessage("");
      setPurchaseStep("init");
      setPaymentRef("");
      setPaymentUssdCode("");
      setPaymentExpiresAt(null);
      setCurrentPurchaseId(null);
      toast.error(
        message || "The payment code expired. Please generate a new one.",
      );
    };

    const hasExpired = () =>
      Number.isFinite(expirationTime) &&
      Date.now() + paymentServerOffsetMs >= expirationTime;

    const markBookAsPaid = () => {
      if (stopped || successfulPaymentHandledRef.current) return;

      successfulPaymentHandledRef.current = true;
      stopped = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (providerTimer) clearTimeout(providerTimer);
      clearActiveBookPayment();
      clearDismissedBookPayment();

      setPurchasedIds((previous) => {
        const next = new Set(previous);
        next.add(String(book.id));
        return next;
      });
      setAutoCheckingPayment(false);
      setPaymentCheckMessage("Payment confirmed. Unlocking your book...");
      toast.success("Payment confirmed! Your book is now unlocked.");
      closePremiumRef.current?.();
      openPdfRef.current?.(book);
    };

    const scheduleLocalCheck = (delay = 8000) => {
      if (stopped) return;
      if (pollTimer) clearTimeout(pollTimer);
      pollTimer = setTimeout(checkLocalPurchaseStatus, delay);
    };

    const scheduleProviderCheck = (delay = 12000) => {
      if (stopped) return;
      if (providerTimer) clearTimeout(providerTimer);
      providerTimer = setTimeout(verifyWithProvider, delay);
    };

    const checkLocalPurchaseStatus = async () => {
      if (stopped) return;

      if (hasExpired()) {
        stopForExpiredCode();
        return;
      }

      if (paymentStatusCheckInFlightRef.current) {
        scheduleLocalCheck(2000);
        return;
      }

      paymentStatusCheckInFlightRef.current = true;

      try {
        const response = await apiClient.get(`/books/${book.id}/purchased`);

        const purchased =
          response.data?.purchased === true ||
          response.data?.data?.purchased === true;

        if (purchased) {
          markBookAsPaid();
          return;
        }

        setPaymentCheckMessage(
          "Waiting for payment confirmation. We are checking automatically.",
        );
      } catch (error) {
        if (error.response?.status === 401) {
          stopped = true;
          setAutoCheckingPayment(false);
          setPaymentCheckMessage("Your session expired. Please log in again.");
          toast.error("Your login session has expired. Please log in again.");
          return;
        }

        // A temporary network or server failure must not falsely fail a
        // payment. The next scheduled local check will retry.
        setPaymentCheckMessage(
          "Connection interrupted. We will keep checking your payment.",
        );
      } finally {
        paymentStatusCheckInFlightRef.current = false;
      }

      scheduleLocalCheck();
    };

    const verifyWithProvider = async () => {
      if (
        stopped ||
        providerVerificationInFlightRef.current ||
        (!currentPurchaseId && !paymentRef)
      ) {
        if (!stopped) scheduleProviderCheck();
        return;
      }

      // Focus and visibility events often arrive together. Throttle them so
      // one return from the dialer creates at most one Monime verification.
      const now = Date.now();
      if (now - lastProviderCheckAtRef.current < 8000) {
        scheduleProviderCheck(8000);
        return;
      }

      lastProviderCheckAtRef.current = now;
      providerVerificationInFlightRef.current = true;

      try {
        const payload = currentPurchaseId
          ? { purchase_id: currentPurchaseId }
          : { payment_reference: paymentRef };
        const response = await apiClient.post(
          "/books/purchase/confirm",
          payload,
        );

        if (response.data?.purchased === true) {
          markBookAsPaid();
          return;
        }

        setPaymentCheckMessage(
          "Payment is still pending. We will continue checking automatically.",
        );
        scheduleLocalCheck(2000);
        scheduleProviderCheck();
      } catch (error) {
        const status = error.response?.status;

        if (status === 402 || status === 410) {
          stopForExpiredCode(
            error.response?.data?.message ||
              "The payment code expired. Please generate a new one.",
          );
        } else if (status === 401) {
          stopped = true;
          setAutoCheckingPayment(false);
          setPaymentCheckMessage("Your session expired. Please log in again.");
          toast.error("Your login session has expired. Please log in again.");
        } else {
          // The webhook/local-status path may still complete the purchase.
          scheduleLocalCheck(2000);
          scheduleProviderCheck();
        }
      } finally {
        providerVerificationInFlightRef.current = false;
      }
    };

    const handleAppReturn = () => {
      if (document.visibilityState === "visible" && !stopped) {
        verifyWithProvider();
        scheduleLocalCheck(1000);
      }
    };

    window.addEventListener("focus", handleAppReturn);
    document.addEventListener("visibilitychange", handleAppReturn);
    scheduleLocalCheck(1200);
    scheduleProviderCheck(5000);

    return () => {
      stopped = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (providerTimer) clearTimeout(providerTimer);
      window.removeEventListener("focus", handleAppReturn);
      document.removeEventListener("visibilitychange", handleAppReturn);
    };
  }, [
    autoCheckingPayment,
    currentPurchaseId,
    isOnline,
    paymentExpiresAt,
    paymentRef,
    paymentServerOffsetMs,
    premiumBook,
    purchaseStep,
  ]);

  // Compute remaining time from the absolute Monime expiry timestamp on
  // every tick. This does not drift while the browser is backgrounded, and
  // the server clock offset avoids relying on an inaccurate phone clock.
  useEffect(() => {
    if (purchaseStep !== "pay" || !paymentExpiresAt) {
      setPaymentSecondsRemaining(null);
      return undefined;
    }

    const expirationTime = Date.parse(paymentExpiresAt);

    if (!Number.isFinite(expirationTime)) {
      setPaymentSecondsRemaining(null);
      return undefined;
    }

    const updateCountdown = () => {
      const correctedNow = Date.now() + paymentServerOffsetMs;
      setPaymentSecondsRemaining(
        Math.max(0, Math.ceil((expirationTime - correctedNow) / 1000)),
      );
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [paymentExpiresAt, paymentServerOffsetMs, purchaseStep]);

  const handleDocumentError = () => {
    setPdfLoading(false);
    setPdfError(true);
  };

  // Memoized collections keep progress updates on one download button from
  // repeatedly filtering the entire book list.
  const favoriteIdSet = useMemo(
    () => new Set(favorites.map((id) => String(id))),
    [favorites],
  );
  const downloadedIdSet = useMemo(
    () => new Set(downloadedIds.map((id) => String(id))),
    [downloadedIds],
  );
  const sectors = useMemo(() => {
    const uniqueSectors = [
      ...new Set(books.map((book) => book.sector).filter(Boolean)),
    ].sort();
    return ["Favorites", "All", ...uniqueSectors, "Premium"];
  }, [books]);
  const filteredBooks = useMemo(() => {
    // Online searching is performed by the database/API. Keep the local
    // version only for cached offline browsing, and search every visible field.
    const normalizedSearch = !isOnline ? normalizeClientSearch(search) : "";

    return books.filter((book) => {
      const matchesSearch =
        !normalizedSearch ||
        normalizeClientSearch(book.title).includes(normalizedSearch) ||
        normalizeClientSearch(book.author).includes(normalizedSearch) ||
        normalizeClientSearch(book.subject).includes(normalizedSearch) ||
        normalizeClientSearch(book.sector).includes(normalizedSearch) ||
        normalizeClientSearch(book.description).includes(normalizedSearch);
      let matchesFilter = false;

      if (filterSector === "All") matchesFilter = true;
      else if (filterSector === "Favorites") {
        matchesFilter = favoriteIdSet.has(String(book.id));
      } else if (filterSector === "Premium") {
        matchesFilter = book.premium === true;
      } else if (filterSector === "Science") {
        matchesFilter = SCIENCE_SECTORS.includes(book.sector);
      } else {
        matchesFilter = book.sector === filterSector;
      }

      return matchesSearch && matchesFilter;
    });
  }, [books, favoriteIdSet, filterSector, isOnline, search]);

  // This is intentionally the first render guard. It guarantees that the
  // initial request displays book-card skeletons and never a text loader.
  if (loading && books.length === 0) {
    return <InitialLibrarySkeleton />;
  }

  // A full-page error is only needed when there are no cached or live books.
  if (error && books.length === 0 && !loading) {
    // Friendly error message with retry
    return (
      <div className="max-w-7xl mx-auto px-3 py-12 text-center">
        <div className="bg-white rounded-xl border border-red-100 p-6 max-w-md mx-auto shadow-sm">
          <div className="text-red-500 text-5xl mb-4">📚</div>
          <p className="text-red-600 font-medium">{error}</p>
          <button
            onClick={() => fetchBooks()}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Hide sidebar */
        .rpv-default-layout__sidebar { display: none !important; }
        .rpv-default-layout__toolbar-sidebar-toggle { display: none !important; }
        [aria-label="Toggle sidebar"], [title="Toggle sidebar"] { display: none !important; }

        /* Backup CSS in case any download/print elements sneak through */
        .rpv-core__download-button,
        [aria-label="Download"],
        [title="Download"],
        .rpv-core__print-button,
        [aria-label="Print"],
        [title="Print"],
        .rpv-core__menu-item[aria-label="Download"],
        .rpv-core__menu-item[aria-label="Print"] {
          display: none !important;
        }

        /* Full-width viewer */
        .rpv-default-layout__main { width: 100% !important; margin-left: 0 !important; }
        .rpv-default-layout__body { grid-template-columns: 0fr 1fr !important; }
        .rpv-default-layout__sidebar { width: 0 !important; min-width: 0 !important; overflow: hidden !important; }

        /* Modal transitions */
        .sheet-transition { transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease; }
        .backdrop-transition { transition: opacity 0.3s ease; }

        /* PDF scaling */
        .rpv-core__viewer {
          height: 100% !important;
          width: 100% !important;
        }
        .rpv-core__inner-page {
          max-width: 100% !important;
        }
        .rpv-core__page-layer {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .rpv-core__canvas-layer {
          max-width: 100%;
          height: auto !important;
        }
        .rpv-core__canvas-layer canvas {
          max-width: 100%;
          height: auto !important;
          width: auto !important;
        }

        /* Smooth social-feed book loading */
        @keyframes book-card-enter {
          from {
            opacity: 0;
            transform: translateY(18px) scale(0.985);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes book-skeleton-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .book-card-enter {
          animation: book-card-enter 0.48s cubic-bezier(0.22, 1, 0.36, 1) backwards;
          backface-visibility: hidden;
        }
        .book-card-optimized {
          content-visibility: auto;
          contain-intrinsic-size: 280px;
          contain: layout paint style;
        }
        .book-skeleton-shimmer {
          background: linear-gradient(
            90deg,
            #f3f4f6 25%,
            #ffffff 50%,
            #f3f4f6 75%
          );
          background-size: 200% 100%;
          animation: book-skeleton-shimmer 1.35s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .book-card-enter,
          .book-skeleton-shimmer {
            animation: none !important;
          }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-2 sm:pt-3 md:pt-4 pb-4 sm:pb-6 space-y-4 sm:space-y-5">
        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <LibrarySearch
            onDebouncedSearch={handleDebouncedSearch}
            resetKey={searchResetKey}
          />
          <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1 sm:pb-0 -mx-1 px-1 sm:mx-0 sm:px-0">
            {sectors.map((sector) => {
              const isFavorites = sector === "Favorites";
              const count = isFavorites ? favorites.length : 0;
              return (
                <button
                  key={sector}
                  onClick={() => {
                    setSearch("");
                    setSearchResetKey((current) => current + 1);
                    setFilterSector(sector);
                  }}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap transition-colors border ${
                    filterSector === sector
                      ? "bg-primary-600 text-white border-primary-600"
                      : "bg-white text-primary-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {sector}
                  {isFavorites && count > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-red-500 text-white rounded-full -top-1 -right-1 absolute">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Network status indicator */}
        <div className="text-xs text-gray-500 flex justify-end items-center gap-1">
          <span
            className={`inline-block w-2 h-2 rounded-full ${isOnline ? "bg-green-500" : "bg-red-500"}`}
          ></span>
          {isOnline ? "Online" : "Offline"}
        </div>

        {/* Book Grid */}
        {loading ? (
          <div
            className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5"
            aria-label="Loading books"
            aria-busy="true"
          >
            {Array.from({ length: INITIAL_SKELETON_COUNT }).map((_, index) => (
              <BookCardSkeleton key={`search-skeleton-${index}`} index={index} />
            ))}
          </div>
        ) : filteredBooks.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
            {filterSector === "Favorites" ? (
              <>
                <HeartOutline
                  className="w-24 h-24 text-red-300 mx-auto mb-4"
                  strokeWidth={1.5}
                />
                <p className="text-gray-600 text-lg font-medium">
                  No favorites yet
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Tap the heart on any book to add it here.
                </p>
              </>
            ) : (
              <>
                <BookOpenIcon
                  className="w-24 h-24 text-gray-300 mx-auto mb-4"
                  strokeWidth={1.5}
                />
                <p className="text-gray-600 text-lg font-medium">
                  No books found
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Try adjusting your search or filter.
                </p>
              </>
            )}
          </div>
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5"
            aria-busy={loadingMore}
          >
            {filteredBooks.map((book, index) => {
              const bookKey = String(book.id);
              const isFav = favoriteIdSet.has(bookKey);
              const isPurchased = purchasedIds.has(bookKey);
              const isDownloaded = downloadedIdSet.has(bookKey);
              const downloadState = downloadStates[bookKey];
              const isDownloading = downloadState?.status === "downloading";
              const downloadFailed = downloadState?.status === "error";
              const downloadProgress = downloadState?.progress;

              return (
                <div
                  key={book.id}
                  onClick={() => handleBookClick(book)}
                  className={`book-card-enter book-card-optimized group bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-primary-200 hover:-translate-y-0.5 hover:shadow-md transform-gpu transition-[transform,box-shadow,border-color] duration-200 ease-out cursor-pointer ${
                    book.premium ? "opacity-90" : ""
                  }`}
                  style={{
                    animationDelay: `${(index % BOOKS_PER_PAGE) * 22}ms`,
                  }}
                >
                  <div className="relative h-36 sm:h-44 md:h-48 bg-gray-50 flex items-center justify-center overflow-hidden">
                    <BookCover
                      key={book.cover || `book-cover-${bookKey}`}
                      book={book}
                      index={index}
                    />

                    {/* Favorite button */}
                    <button
                      onClick={(e) => toggleFavorite(book.id, e)}
                      className="absolute top-1.5 sm:top-2 right-1.5 sm:right-2 p-1 bg-white/80 rounded-full hover:bg-white transition-colors z-10 border border-gray-200 shadow-sm"
                    >
                      {isFav ? (
                        <HeartSolid className="w-4 sm:w-5 h-4 sm:h-5 text-red-500" />
                      ) : (
                        <HeartOutline className="w-4 sm:w-5 h-4 sm:h-5 text-gray-500 hover:text-red-400" />
                      )}
                    </button>

                    {/* Download button (only for free or purchased premium books) */}
                    {(!book.premium || isPurchased) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isDownloaded && !isDownloading) {
                            downloadBook(book);
                          }
                        }}
                        disabled={isDownloaded || isDownloading}
                        className={`absolute bottom-1.5 sm:bottom-2 left-1.5 sm:left-2 w-9 h-9 sm:w-10 sm:h-10 rounded-full z-10 border shadow-sm flex items-center justify-center transition-[background-color,border-color,color,transform] duration-200 ${
                          isDownloaded
                            ? "bg-emerald-50/95 border-emerald-300 cursor-default"
                            : isDownloading
                              ? "bg-white/95 border-primary-200 cursor-wait"
                              : downloadFailed
                                ? "bg-red-50/95 border-red-300 hover:bg-red-100 active:scale-95"
                                : "bg-white/90 border-gray-200 hover:bg-white hover:border-primary-300 active:scale-95"
                        }`}
                        title={
                          isDownloaded
                            ? "Available offline"
                            : isDownloading
                              ? Number.isFinite(downloadProgress)
                                ? `Downloading ${downloadProgress}%`
                                : "Downloading"
                              : downloadFailed
                                ? downloadState.message ||
                                  "Download failed. Tap to retry."
                                : "Download for offline use"
                        }
                        aria-label={
                          isDownloaded
                            ? `${book.title} is available offline`
                            : isDownloading
                              ? `Downloading ${book.title}`
                              : `Download ${book.title}`
                        }
                        aria-busy={isDownloading}
                      >
                        {isDownloading ? (
                          <>
                            <svg
                              viewBox="0 0 36 36"
                              className={`absolute inset-0 w-full h-full -rotate-90 ${
                                Number.isFinite(downloadProgress)
                                  ? ""
                                  : "animate-spin"
                              }`}
                              aria-hidden="true"
                            >
                              <circle
                                cx="18"
                                cy="18"
                                r="15"
                                pathLength="100"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                className="text-gray-200"
                              />
                              <circle
                                cx="18"
                                cy="18"
                                r="15"
                                pathLength="100"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeDasharray={
                                  Number.isFinite(downloadProgress)
                                    ? 100
                                    : "24 76"
                                }
                                strokeDashoffset={
                                  Number.isFinite(downloadProgress)
                                    ? 100 - downloadProgress
                                    : 0
                                }
                                className="text-primary-600 transition-[stroke-dashoffset] duration-300 ease-out"
                              />
                            </svg>
                            {Number.isFinite(downloadProgress) ? (
                              <span className="relative text-[9px] sm:text-[10px] font-bold text-primary-700 tabular-nums">
                                {downloadProgress}%
                              </span>
                            ) : (
                              <ArrowDownTrayIcon className="relative w-4 h-4 text-primary-600" />
                            )}
                          </>
                        ) : (
                          <ArrowDownTrayIcon
                            className={`w-4 sm:w-5 h-4 sm:h-5 ${
                              isDownloaded
                                ? "text-emerald-600"
                                : downloadFailed
                                  ? "text-red-600"
                                  : "text-primary-600"
                            }`}
                          />
                        )}
                      </button>
                    )}

                    {/* Premium badge */}
                    {book.premium && (
                      <div className="absolute bottom-1.5 sm:bottom-2 right-1.5 sm:right-2">
                        <span
                          className={`text-white text-xs sm:text-sm px-2.5 py-1 rounded-full font-bold shadow-md inline-flex items-center gap-1 ${
                            isPurchased
                              ? "bg-gradient-to-r from-emerald-500 to-green-500"
                              : "bg-gradient-to-r from-amber-400 to-yellow-500"
                          }`}
                        >
                          {isPurchased ? (
                            <>
                              <LockOpenIcon className="w-3.5 h-3.5" />
                              Owned
                            </>
                          ) : (
                            formatPrice(book.price)
                          )}
                        </span>
                      </div>
                    )}

                    {book.premium && !isPurchased && (
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/30 transition-opacity">
                        <LockClosedIcon className="w-10 h-10 text-white drop-shadow-lg" />
                      </div>
                    )}
                  </div>

                  <div className="p-3 sm:p-4">
                    <h3
                      className="font-semibold text-primary-700 text-sm sm:text-lg truncate"
                      title={book.title}
                    >
                      {book.title}
                    </h3>
                    <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1">
                      <UserIcon className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                      <span className="truncate">{book.author}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2 sm:mt-3">
                      <span className="text-[10px] sm:text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        {book.sector}
                      </span>
                      {book.premium ? (
                        <span
                          className={`text-[10px] sm:text-xs px-2 py-0.5 rounded font-medium flex items-center gap-0.5 ${
                            isPurchased
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {isPurchased ? (
                            <LockOpenIcon className="w-3 h-3" />
                          ) : (
                            <LockClosedIcon className="w-3 h-3" />
                          )}
                          {isPurchased ? "Owned" : "Premium"}
                        </span>
                      ) : (
                        <span className="text-[10px] sm:text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                          Free
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Skeleton cards make the next page feel like a social feed. */}
            {loadingMore &&
              Array.from({ length: INITIAL_SKELETON_COUNT }).map((_, index) => (
                <BookCardSkeleton
                  key={`book-skeleton-${index}`}
                  index={index}
                />
              ))}
          </div>
        )}

        {/* Invisible trigger: intentionally no "all books loaded" message. */}
        <div
          ref={loadMoreTriggerRef}
          className={`flex items-center justify-center ${
            pagination.hasNextPage || loadMoreError ? "min-h-12 py-3" : "h-0"
          }`}
          aria-live="polite"
        >
          {loadMoreError ? (
            <div className="text-center">
              <p className="text-sm text-red-500 mb-2">
                More books could not be loaded.
              </p>
              <button
                type="button"
                onClick={loadNextPage}
                className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Try again
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Premium Modal */}
      {showPremiumModal && premiumBook && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center">
          <div
            className={`absolute inset-0 bg-black/50 backdrop-transition ${isSheetVisible ? "opacity-100" : "opacity-0"}`}
            onClick={handleClosePremium}
          />
          <div
            className={`relative bg-white w-full max-w-full sm:max-w-md lg:max-w-lg rounded-t-2xl lg:rounded-2xl shadow-2xl sheet-transition ${
              isSheetVisible
                ? "translate-y-0"
                : "translate-y-full lg:translate-y-0 lg:scale-95"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-2 lg:hidden">
              <div className="w-12 h-1 bg-gray-300 rounded-full" />
            </div>

            <div className="px-4 sm:px-6 lg:px-8 py-4 lg:py-6 pb-8 lg:pb-10">
              {purchaseStep === "init" && (
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-primary-600 flex items-center justify-center mb-4 shadow-md">
                    <LockClosedIcon
                      className="w-8 h-8 text-white"
                      strokeWidth={1.5}
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    Premium Content
                  </h3>
                  <p className="text-gray-600 mt-2 text-sm leading-relaxed max-w-sm">
                    This book costs{" "}
                    <span className="font-semibold text-primary-600">
                      {formatPrice(premiumBook.price)}
                    </span>
                    . Buy it to unlock full access.
                  </p>
                  <p className="text-sm font-medium text-primary-600 mt-2">
                    “{premiumBook.title}” by {premiumBook.author}
                  </p>
                  <div className="mt-6 w-full flex gap-3">
                    <button
                      onClick={handleClosePremium}
                      className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBuyNow}
                      disabled={purchaseLoading}
                      className="flex-1 py-3 bg-primary-600 text-white rounded-xl font-medium shadow-sm hover:bg-primary-700 disabled:opacity-50"
                    >
                      {purchaseLoading ? "Processing..." : "Buy Now"}
                    </button>
                  </div>
                </div>
              )}

              {purchaseStep === "pay" && (
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                    <CreditCardIcon className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    Complete Payment
                  </h3>
                  <p className="text-gray-600 mt-2 text-sm leading-relaxed max-w-sm">
                    Tap <span className="font-semibold">Pay Now</span> to open
                    your phone dialer and pay{" "}
                    <span className="font-semibold">
                      {formatPrice(
                        paymentAmount ?? premiumBook.price,
                        paymentCurrency || premiumBook.currency,
                      )}
                    </span>
                    . When you return, the app checks the payment automatically.
                  </p>
                  <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl px-5 py-4 w-full max-w-xs">
                    <span className="block text-xl font-mono font-bold text-primary-700 tracking-wide break-all">
                      {paymentUssdCode}
                    </span>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(paymentUssdCode);
                          toast.success("USSD code copied");
                        } catch (_) {
                          toast.error("Unable to copy the USSD code");
                        }
                      }}
                      disabled={!paymentUssdCode}
                      className="mt-3 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-50"
                    >
                      Copy USSD Code
                    </button>
                  </div>
                  {paymentSecondsRemaining !== null && (
                    <div
                      className={`mt-3 rounded-lg px-3 py-2 text-sm font-medium ${
                        paymentSecondsRemaining > 60
                          ? "bg-amber-50 text-amber-700"
                          : "bg-red-50 text-red-700"
                      }`}
                      role="timer"
                      aria-live="off"
                    >
                      Time remaining:{" "}
                      <span className="font-mono font-bold tabular-nums">
                        {formatPaymentCountdown(paymentSecondsRemaining)}
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handlePayNow}
                    disabled={!paymentUssdCode}
                    className="mt-5 w-full py-3.5 bg-primary-600 text-white rounded-xl font-semibold shadow-sm hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <PhoneIcon className="w-5 h-5" />
                    Pay Now
                  </button>
                  {autoCheckingPayment && (
                    <div
                      className="mt-3 w-full rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 flex items-start gap-3 text-left"
                      role="status"
                      aria-live="polite"
                    >
                      <span className="mt-0.5 h-4 w-4 flex-none rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin" />
                      <p className="text-xs leading-relaxed text-blue-700">
                        {paymentCheckMessage ||
                          "Waiting for payment confirmation. We are checking automatically."}
                      </p>
                    </div>
                  )}
                  <div className="mt-6 w-full flex gap-3">
                    <button
                      onClick={handleBackFromPayment}
                      className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleConfirmPayment}
                      disabled={purchaseLoading}
                      className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium shadow-sm hover:bg-green-700 disabled:opacity-50"
                    >
                      {purchaseLoading ? "Checking..." : "Check Payment"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PDF Modal */}
      {selectedBook && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col items-center justify-center">
          <div className="w-full bg-white flex items-center justify-between px-4 py-2 shadow-md flex-shrink-0">
            <h3 className="text-sm font-medium text-primary-700 truncate max-w-[80%]">
              {selectedBook.title}
            </h3>
            <button
              onClick={() => {
                if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
                if (abortControllerRef.current)
                  abortControllerRef.current.abort();
                setSelectedBook(null);
              }}
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <XMarkIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          <div
            className="relative flex-1 w-full bg-white overflow-auto"
            onContextMenu={(e) => e.preventDefault()}
          >
            {pdfLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20">
                <div className="w-3/4 max-w-md">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 transition-all duration-300 ease-out"
                      style={{ width: `${pdfProgress}%` }}
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-600 text-center">
                    {pdfProgress < 100
                      ? `Loading ${Math.round(pdfProgress)}%`
                      : "Rendering..."}
                  </p>
                </div>
              </div>
            )}
            {pdfError && !pdfLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                <div className="text-center text-red-500">
                  <p>Failed to load PDF.</p>
                  <button
                    onClick={() => {
                      const book = selectedBook;
                      setSelectedBook(null);
                      setTimeout(() => openPdf(book), 100);
                    }}
                    className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-lg"
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}
            {selectedBook.pdfUrl && (
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer
                  key={selectedBook.pdfUrl}
                  fileUrl={selectedBook.pdfUrl}
                  plugins={[defaultLayoutPluginInstance]}
                  defaultScale={
                    window.innerWidth < 640
                      ? 0.6
                      : window.innerWidth < 1024
                        ? 0.8
                        : 1.0
                  }
                  onDocumentLoad={() => setPdfLoading(false)}
                  onDocumentError={handleDocumentError}
                />
              </Worker>
            )}
          </div>
        </div>
      )}
    </>
  );
}